import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Card — the one surface primitive.
 *
 * Enterprise surfaces are flat and quiet: a solid 1px border, a barely
 * visible resting shadow, and *no* motion unless the card is a control.
 * Hover treatments, scale transforms, gradient washes and glows were the
 * biggest reason the old catalog / Foundry / Reports grids read as a
 * student project — every static panel reacted to the mouse.
 *
 *   variant="default"   bordered panel, no hover reaction (the norm)
 *   variant="ghost"     no border, resting shadow only (tiles on tinted bg)
 *   variant="elevated"  deeper shadow (callouts, floating summaries)
 *
 *   interactive         the *whole card* is a control: pointer cursor,
 *                       border darkens + shadow lifts on hover, and a
 *                       focus ring for keyboard users. Pass `onClick`
 *                       and the card becomes a button (role, tabIndex,
 *                       Enter/Space) unless `asChild`-style markup is
 *                       supplied by the caller.
 *   selected            persistent ring — multi-select grids
 *   disabled            muted + non-interactive
 *
 * Add new looks here, never via raw className sweeps.
 */
export type CardVariant = "default" | "ghost" | "elevated"

const VARIANT_CLASSES: Record<CardVariant, string> = {
  default: "border border-border shadow-card",
  ghost: "border border-transparent shadow-card",
  elevated: "border border-border shadow-elevated",
}

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant
  /** The whole card acts as a control (hover/focus affordances). */
  interactive?: boolean
  /** Persistent selection ring. */
  selected?: boolean
  /** Muted + inert. Also blocks the onClick handler. */
  disabled?: boolean
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = "default",
      interactive = false,
      selected = false,
      disabled = false,
      onClick,
      onKeyDown,
      ...props
    },
    ref,
  ) => {
    const clickable = interactive && !!onClick && !disabled

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(e)
      if (!clickable || e.defaultPrevented) return
      // Only fire when the card itself has focus — nested buttons,
      // inputs and menus handle their own keys.
      if (e.target !== e.currentTarget) return
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>)
      }
    }

    return (
      <div
        ref={ref}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-disabled={disabled || undefined}
        data-selected={selected || undefined}
        onClick={disabled ? undefined : onClick}
        onKeyDown={handleKeyDown}
        className={cn(
          "rounded-lg bg-card text-card-foreground",
          VARIANT_CLASSES[variant],
          interactive &&
            !disabled &&
            "cursor-pointer transition-[border-color,box-shadow] duration-150 hover:border-primary/40 hover:shadow-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          selected && "border-primary ring-1 ring-primary",
          disabled && "opacity-60 cursor-not-allowed",
          className,
        )}
        {...props}
      />
    )
  },
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1 p-5", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-base font-semibold leading-snug tracking-tight text-foreground",
      className,
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-5 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Hairline above the footer with a faint tint — the standard
   *  "actions strip" at the bottom of a record card. */
  bordered?: boolean
}

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, bordered = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center",
        bordered
          ? "border-t border-border bg-muted/40 px-5 py-3 rounded-b-lg"
          : "p-5 pt-0",
        className,
      )}
      {...props}
    />
  ),
)
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
